/*
  Eva Radio keyboard mode: vanilla WebAudio mini-synth honoring the real
  HW-01 unit's two printed TONE voices (Fantasy Piano / Violin Flute),
  plus a 4-loop sampled rhythm section. Pure audio engine -- no DOM, no
  hotspot knowledge. eva-radio-keyboard.js drives this from key events.

  One shared AudioContext for both the synth voices and the rhythm
  loops (same theme-wide "create/resume on first user gesture" rule
  everything else on this widget already follows).
*/
(function () {
  var MAX_VOICES = 6;
  // Base levels before the master keyboard-volume knob below: both
  // already cut 25% (×0.75) per Per's first pass -- the synth sat louder
  // than most loudness-normalized tape tracks. 0.5 * 0.75 = 0.375;
  // rhythm loops' own base level was a bare 1 (full), now 0.75.
  var BASE_MASTER_GAIN = 0.375;
  var BASE_RHYTHM_GAIN = 0.75;
  // 2026-08-06: the ONE tunable knob for "how loud is the keyboard
  // overall" -- Per asked for it noticeably under tape playback level,
  // exposed as a single constant rather than requiring the two gains
  // above to be retuned in lockstep every time. Tape's own per-track
  // volume is audio.volume = dbToLinear(gainDb) (see eva-radio.js's
  // updateAudioVolume()) -- real tracks in this catalog run gainDb
  // roughly -0.5 to -9, i.e. a linear volume of about 0.35-0.95, so
  // "noticeably under" means comfortably below that floor, not just
  // under its average. At 0.55 the note peak lands at 0.375*0.55=0.206
  // and rhythm loops at 0.75*0.55=0.413 -- both clear of the tape range.
  var KEYBOARD_MASTER_VOLUME = 0.55;
  var MASTER_GAIN = BASE_MASTER_GAIN * KEYBOARD_MASTER_VOLUME;
  var RHYTHM_GAIN = BASE_RHYTHM_GAIN * KEYBOARD_MASTER_VOLUME;
  var LOWPASS_HZ = 6000; // gentle cheap-speaker character, matches the unit's own tiny drivers

  var audioCtx = null;
  var masterGain = null;
  var lowpass = null;
  var rhythmGain = null;

  var voiceName = 'fantasyPiano'; // | 'violinFlute'
  var activeVoices = []; // [{id, osc, osc2, gain, note}], oldest first
  var rhythmSources = {}; // key -> {source, gain} for currently-looping rhythm buffers
  var rhythmBuffers = {}; // key -> decoded AudioBuffer, cached after first load
  var duckFactor = 1;
  var muted = false;

  function ensureContext() {
    if (audioCtx) { return audioCtx; }
    var AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) { return null; }
    audioCtx = new AC();
    masterGain = audioCtx.createGain();
    masterGain.gain.value = MASTER_GAIN;
    lowpass = audioCtx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = LOWPASS_HZ;
    rhythmGain = audioCtx.createGain();
    rhythmGain.gain.value = RHYTHM_GAIN;
    masterGain.connect(lowpass);
    lowpass.connect(audioCtx.destination);
    rhythmGain.connect(lowpass);
    return audioCtx;
  }

  function resume() {
    var ctx = ensureContext();
    if (ctx && ctx.state === 'suspended') { ctx.resume().catch(function () {}); }
    return ctx;
  }

  // --- Note name -> frequency ---------------------------------------

  var NOTE_INDEX = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  function noteToFreq(note) {
    // note like "F#3" or "C4"
    var m = /^([A-G])(#?)(-?\d+)$/.exec(note);
    if (!m) { return 440; }
    var semitone = NOTE_INDEX[m[1]] + (m[2] === '#' ? 1 : 0);
    var octave = parseInt(m[3], 10);
    var midi = (octave + 1) * 12 + semitone;
    return 440 * Math.pow(2, (midi - 69) / 12);
  }

  // --- Voices ----------------------------------------------------------
  // fantasyPiano: square osc, punchy exponential decay, no sustain --
  // reads as a bright plinky toy-piano pluck.
  // violinFlute: triangle + a touch of the 2nd harmonic (sine at 2f),
  // slow attack, sustains while held, gentle vibrato -- reads as a
  // breathy, sustained tone.
  function startVoice(freq) {
    var ctx = resume();
    if (!ctx) { return null; }
    var now = ctx.currentTime;
    var gain = ctx.createGain();
    gain.connect(masterGain);

    if (voiceName === 'violinFlute') {
      var osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      var osc2 = ctx.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.value = freq * 2;
      var osc2Gain = ctx.createGain();
      osc2Gain.gain.value = 0.08;
      osc2.connect(osc2Gain);
      osc2Gain.connect(gain);

      var vibrato = ctx.createOscillator();
      vibrato.frequency.value = 5.5;
      var vibratoDepth = ctx.createGain();
      vibratoDepth.gain.value = freq * 0.035;
      vibrato.connect(vibratoDepth);
      vibratoDepth.connect(osc.frequency);
      vibratoDepth.connect(osc2.frequency); // keep the harmonic locked to the same vibrato

      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(1 * duckFactor * (muted ? 0 : 1), now + 0.06); // 60ms attack, sustain while held

      osc.connect(gain);
      osc.start(now);
      osc2.start(now);
      vibrato.start(now);

      return { osc: osc, osc2: osc2, vibrato: vibrato, gain: gain, sustained: true };
    }

    // fantasyPiano
    var oscP = ctx.createOscillator();
    oscP.type = 'square';
    oscP.frequency.value = freq;
    var peak = 1 * duckFactor * (muted ? 0 : 1);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(peak, now + 0.004); // 4ms attack
    // exponential decay, tau ~220ms -- setTargetAtTime never truly reaches
    // 0, so tail off toward a near-silent floor rather than literal zero
    // (exponentialRampToValueAtTime can't target 0 directly).
    gain.gain.setTargetAtTime(0.0001, now + 0.004, 0.22);

    oscP.connect(gain);
    oscP.start(now);

    return { osc: oscP, gain: gain, sustained: false };
  }

  function stopVoice(v, releaseSeconds) {
    if (!v || v.stopped) { return; }
    v.stopped = true;
    var ctx = audioCtx;
    if (!ctx) { return; }
    var now = ctx.currentTime;
    var rel = releaseSeconds || 0.03;
    try {
      v.gain.gain.cancelScheduledValues(now);
      var current = v.gain.gain.value;
      v.gain.gain.setValueAtTime(current, now);
      v.gain.gain.linearRampToValueAtTime(0, now + rel);
    } catch (e) {}
    var stopAt = now + rel + 0.02;
    try { v.osc.stop(stopAt); } catch (e) {}
    if (v.osc2) { try { v.osc2.stop(stopAt); } catch (e) {} }
    if (v.vibrato) { try { v.vibrato.stop(stopAt); } catch (e) {} }
  }

  // --- Public API --------------------------------------------------------

  function noteOn(id, note) {
    var ctx = resume();
    if (!ctx) { return; }
    // steal oldest if at polyphony limit
    while (activeVoices.length >= MAX_VOICES) {
      var oldest = activeVoices.shift();
      stopVoice(oldest.v, 0.02);
    }
    var freq = noteToFreq(note);
    var v = startVoice(freq);
    if (!v) { return; }
    activeVoices.push({ id: id, v: v, note: note });
  }

  function noteOff(id) {
    for (var i = 0; i < activeVoices.length; i++) {
      if (activeVoices[i].id === id) {
        var entry = activeVoices[i];
        var releaseSeconds = entry.v.sustained ? 0.12 : 0.02; // violinFlute release=120ms; fantasyPiano already decaying, short tail-cut is enough
        stopVoice(entry.v, releaseSeconds);
        activeVoices.splice(i, 1);
        return;
      }
    }
  }

  function allNotesOff() {
    activeVoices.forEach(function (entry) { stopVoice(entry.v, 0.03); });
    activeVoices = [];
  }

  function setVoice(name) {
    voiceName = name === 'violinFlute' ? 'violinFlute' : 'fantasyPiano';
  }

  function getVoice() { return voiceName; }

  function duck(factor) {
    duckFactor = factor;
    if (masterGain) { masterGain.gain.value = MASTER_GAIN * duckFactor * (muted ? 0 : 1); }
  }

  function restoreVolume() {
    duckFactor = 1;
    if (masterGain) { masterGain.gain.value = MASTER_GAIN * (muted ? 0 : 1); }
  }

  function setMuted(isMuted) {
    muted = isMuted;
    if (masterGain) { masterGain.gain.value = MASTER_GAIN * duckFactor * (muted ? 0 : 1); }
    if (rhythmGain) { rhythmGain.gain.value = muted ? 0 : RHYTHM_GAIN; }
  }

  // --- Rhythm loops --------------------------------------------------
  // AudioBufferSourceNode per loop, decoded once and cached. Looping
  // preset behaves like the real unit's RHYTHM SELECT row: tap the
  // active one to stop, tap a different one to switch.

  function loadRhythmBuffer(key, url, cb) {
    var ctx = resume();
    if (!ctx || !url) { cb(null); return; }
    if (rhythmBuffers[key]) { cb(rhythmBuffers[key]); return; }
    fetch(url).then(function (r) {
      if (!r.ok) { throw new Error('rhythm fetch failed'); }
      return r.arrayBuffer();
    }).then(function (buf) {
      return ctx.decodeAudioData(buf);
    }).then(function (decoded) {
      rhythmBuffers[key] = decoded;
      cb(decoded);
    }).catch(function () { cb(null); });
  }

  function startRhythm(key, url) {
    var ctx = resume();
    if (!ctx) { return; }
    stopRhythm(key);
    loadRhythmBuffer(key, url, function (buffer) {
      if (!buffer || !ctx) { return; }
      var source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      var gain = ctx.createGain();
      gain.gain.value = 1;
      source.connect(gain);
      gain.connect(rhythmGain);
      source.start(0);
      rhythmSources[key] = { source: source, gain: gain };
    });
  }

  function stopRhythm(key) {
    var entry = rhythmSources[key];
    if (!entry) { return; }
    try { entry.source.stop(); } catch (e) {}
    delete rhythmSources[key];
  }

  function stopAllRhythm() {
    Object.keys(rhythmSources).forEach(stopRhythm);
  }

  function isRhythmPlaying(key) {
    return !!rhythmSources[key];
  }

  window.EvaSynth = {
    noteOn: noteOn,
    noteOff: noteOff,
    allNotesOff: allNotesOff,
    setVoice: setVoice,
    getVoice: getVoice,
    duck: duck,
    restore: restoreVolume,
    setMuted: setMuted,
    startRhythm: startRhythm,
    stopRhythm: stopRhythm,
    stopAllRhythm: stopAllRhythm,
    isRhythmPlaying: isRhythmPlaying,
    resume: resume
  };
})();
